import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserActivitySummary, UserActivitySummaryDTO } from "../api/adminActivity";
import { isAdminUser } from "../helpers/isAdmin";

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const clampNumber = (val: number, min: number, max: number) =>
  Math.min(max, Math.max(min, val));

const EarlyUserIncentives: React.FC = () => {
  const navigate = useNavigate();

  const [userIdInput, setUserIdInput] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [defaultDays, setDefaultDays] = useState(90);
  const [rate, setRate] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<UserActivitySummaryDTO | null>(null);

  const isAdmin = useMemo(() => isAdminUser(), []);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/", { replace: true });
    }
  }, [isAdmin, navigate]);

  const parsedUserId = useMemo(() => {
    const trimmed = userIdInput.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.floor(n);
  }, [userIdInput]);

  const reward = useMemo(() => {
    const activeDays = data?.activeDays ?? 0;
    const safeRate = Number.isFinite(rate) ? rate : 0;
    return activeDays * Math.max(0, safeRate);
  }, [data?.activeDays, rate]);

  const activationNote = useMemo(() => {
    if (!data) return "";
    if (!data.days || data.days.length === 0) return "No activity recorded for this user in the selected date range.";
    return "";
  }, [data]);

  const handleFetch = async () => {
    setError("");
    setData(null);

    if (!isAdmin) {
      setError("Admin only.");
      return;
    }

    if (!parsedUserId) {
      setError("Enter a valid userId.");
      return;
    }

    // Basic validation: if only one side of range is provided, allow backend defaults.
    // If both are provided, ensure from <= to.
    if (from && to && from > to) {
      setError("From date cannot be after To date.");
      return;
    }

    try {
      setLoading(true);
      const res = await getUserActivitySummary({
        userId: parsedUserId,
        from: from || undefined,
        to: to || undefined,
        defaultDays: !from ? clampNumber(defaultDays, 1, 365) : undefined,
      });
      setData(res);
    } catch (e: any) {
      const msg = String(e?.message || "");
      // Axios errors from apiService may come through as response objects.
      const status = e?.status || e?.statusCode || e?.data?.status || e?.response?.status;

      if (status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      if (status === 403) {
        setError("Admin only.");
        return;
      }

      setError(msg || "Failed to load analytics. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(108,99,255,0.20) 0%, rgba(139,92,246,0.12) 35%, #020617 100%)",
        padding: "84px 16px 40px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(22px, 3.6vw, 34px)",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "-0.6px",
              }}
            >
              Early User Incentives
            </h1>
            <p style={{ margin: "8px 0 0", color: "rgba(199,210,254,0.92)", fontWeight: 500 }}>
              Admin-only activity summary (UTC days).
            </p>
          </div>

          <button
            onClick={() => navigate(-1)}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 12,
              cursor: "pointer",
              fontWeight: 700,
              backdropFilter: "blur(10px)",
            }}
          >
            Back
          </button>
        </div>

        <div
          style={{
            background: "rgba(15,23,42,0.78)",
            border: "1px solid rgba(108,99,255,0.28)",
            borderRadius: 20,
            boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
            padding: 18,
            backdropFilter: "blur(14px)",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 12,
              alignItems: "end",
            }}
          >
            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", color: "rgba(199,210,254,0.9)", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                User ID
              </label>
              <input
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                placeholder="e.g. 123"
                inputMode="numeric"
                style={{
                  width: "100%",
                  padding: "12px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", color: "rgba(199,210,254,0.9)", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                From (optional)
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", color: "rgba(199,210,254,0.9)", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                To (optional)
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", color: "rgba(199,210,254,0.9)", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                Default days (when From is empty)
              </label>
              <input
                type="number"
                value={defaultDays}
                min={1}
                max={365}
                onChange={(e) => setDefaultDays(Number(e.target.value))}
                disabled={!!from}
                style={{
                  width: "100%",
                  padding: "12px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: from ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.06)",
                  color: from ? "rgba(255,255,255,0.55)" : "#fff",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ gridColumn: "span 3" }}>
              <label style={{ display: "block", color: "rgba(199,210,254,0.9)", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                Reward rate (₦ / active day)
              </label>
              <input
                type="number"
                value={rate}
                min={0}
                onChange={(e) => setRate(Number(e.target.value))}
                style={{
                  width: "100%",
                  padding: "12px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ gridColumn: "span 9", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setUserIdInput("");
                  setFrom("");
                  setTo("");
                  setDefaultDays(90);
                  setRate(0);
                  setError("");
                  setData(null);
                }}
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.06)",
                  color: "rgba(255,255,255,0.88)",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Reset
              </button>
              <button
                onClick={handleFetch}
                disabled={loading}
                style={{
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "none",
                  background: loading
                    ? "rgba(108,99,255,0.55)"
                    : "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                  color: "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: 900,
                  boxShadow: "0 10px 30px rgba(108,99,255,0.25)",
                }}
              >
                {loading ? "Loading..." : "Fetch activity"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "rgba(248,113,113,0.14)",
              border: "1px solid rgba(248,113,113,0.55)",
              color: "#fecaca",
              padding: "14px 16px",
              borderRadius: 16,
              marginBottom: 14,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        )}

        {data && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 18,
                  padding: 16,
                  color: "#fff",
                }}
              >
                <div style={{ color: "rgba(199,210,254,0.9)", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                  Active days
                </div>
                <div style={{ fontSize: 34, fontWeight: 900, marginTop: 6 }}>{data.activeDays ?? 0}</div>
                <div style={{ color: "rgba(199,210,254,0.85)", fontWeight: 600, marginTop: 6 }}>
                  Range: {data.from} → {data.to}
                </div>
              </div>

              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 18,
                  padding: 16,
                  color: "#fff",
                }}
              >
                <div style={{ color: "rgba(199,210,254,0.9)", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                  Reward
                </div>
                <div style={{ fontSize: 34, fontWeight: 900, marginTop: 6 }}>
                  ₦{reward.toLocaleString()}
                </div>
                <div style={{ color: "rgba(199,210,254,0.85)", fontWeight: 600, marginTop: 6 }}>
                  Rate: ₦{Math.max(0, rate).toLocaleString()} / day
                </div>
              </div>
            </div>

            {activationNote && (
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "rgba(199,210,254,0.95)",
                  padding: "14px 16px",
                  borderRadius: 16,
                  marginBottom: 14,
                  fontWeight: 700,
                }}
              >
                {activationNote}
              </div>
            )}

            <div
              style={{
                background: "rgba(15,23,42,0.78)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 20,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                  color: "#fff",
                  fontWeight: 900,
                  letterSpacing: "-0.2px",
                }}
              >
                Daily activity
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.05)" }}>
                      {["Date", "First seen", "Last seen", "Hit count"].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "12px 14px",
                            fontSize: 12,
                            color: "rgba(199,210,254,0.95)",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            fontWeight: 900,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.days || []).map((d) => (
                      <tr key={d.date} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                        <td style={{ padding: "12px 14px", color: "#fff", fontWeight: 800 }}>{d.date}</td>
                        <td style={{ padding: "12px 14px", color: "rgba(255,255,255,0.88)", fontWeight: 600 }}>
                          {formatDateTime(d.firstSeenAt)}
                        </td>
                        <td style={{ padding: "12px 14px", color: "rgba(255,255,255,0.88)", fontWeight: 600 }}>
                          {formatDateTime(d.lastSeenAt)}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#fff", fontWeight: 900 }}>{d.hitCount ?? 0}</td>
                      </tr>
                    ))}
                    {(data.days || []).length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: "18px 14px", color: "rgba(199,210,254,0.9)", fontWeight: 700 }}>
                          No activity recorded for this user in the selected date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EarlyUserIncentives;

